<script setup lang="ts">
import { computed, defineAsyncComponent, ref } from 'vue';
import { useRouter } from 'vue-router';

import NewSessionButtons from '@/components/sidebar/NewSessionButtons.vue';
import SearchChatButton from '@/components/sidebar/SearchChatButton.vue';
import { useToast } from '@/components/ui/toast';
import { useDocumentSession } from '@/composables/useDocumentSession';
import { useAccessContextStore } from '@/stores/accessContextStore';
import { useAgentStore } from '@/stores/agentStore';
import { useConfigCatStore } from '@/stores/configCat';
import { useSavedPromptStore } from '@/stores/savedPrompts';
import { useTenantStore } from '@/stores/tenant';

const UnifiedSessions = defineAsyncComponent(
    () => import('@/components/UnifiedSessions.vue')
);

const props = defineProps<{
    isSidebarOpen: boolean;
}>();

const agentStore = useAgentStore();
const tenantStore = useTenantStore();
const savedPromptStore = useSavedPromptStore();
const accessContextStore = useAccessContextStore();
const configCatStore = useConfigCatStore();
const router = useRouter();
const toast = useToast();

const unifiedSessionsRef = ref<InstanceType<typeof UnifiedSessions> | null>(
    null
);

const tenantId = computed(() => tenantStore.tenantId);

const isCreatingPresentation = ref(false);

const {
    createDocumentSession,
    cleanupOrphanedResources,
    redirectToViewer,
    resetPendingCreationState
} = useDocumentSession();

async function handleNewChat() {
    router.push(`/chat`);
    savedPromptStore.shouldEmptyInput = true;
    agentStore.isDeepResearchEnabled = false;
    agentStore.isReasoningEnabled = false;
}

/**
 * Show error toast with consistent messaging
 */
function showErrorToast(title: string, description?: string) {
    toast.toast({
        title,
        description: description || 'Please try again later',
        variant: 'destructive'
    });
}

async function handleSlideStudioClick() {
    if (!tenantId.value) return;

    try {
        isCreatingPresentation.value = true;

        const session = await createDocumentSession('slides', 'New slides', {
            usePendingChatSession: true
        });
        savedPromptStore.shouldEmptyInput = true;
        agentStore.isDeepResearchEnabled = false;
        agentStore.isReasoningEnabled = false;

        redirectToViewer(session.chatSession.id, session.document.id, 'slides');
    } catch (error) {
        // Don't show error if it was just an abort
        if (error instanceof Error && error.name === 'AbortError') {
            return;
        }
        console.error('Error creating slide studio presentation:', error);
        // Clean up any orphaned resources (e.g., chat session created but document failed)
        await cleanupOrphanedResources('session');
        showErrorToast(
            'Error',
            'Failed to create Slide Studio presentation. Please try again.'
        );
    } finally {
        isCreatingPresentation.value = false;
        resetPendingCreationState('session');
    }
}

function handleNewPresentation() {
    const provider = accessContextStore.billingProvider;
    if (provider === 'stripe' || !configCatStore.isSlideEditorEnabled) {
        handleSlideStudioClick();
    } else {
        router.push('/presentation');
    }
}
</script>

<template>
    <div class="flex flex-col gap-2 h-full min-h-0">
        <NewSessionButtons
            :is-sidebar-open="props.isSidebarOpen"
            :is-creating-presentation="isCreatingPresentation"
            @create-chat="handleNewChat"
            @create-presentation="handleNewPresentation"
        />

        <SearchChatButton :is-sidebar-open="props.isSidebarOpen" />

        <div
            class="flex-1 overflow-hidden transition-opacity duration-300"
            :class="props.isSidebarOpen ? 'opacity-100' : 'opacity-0'"
        >
            <UnifiedSessions ref="unifiedSessionsRef" />
        </div>
    </div>
</template>
